import { useRef, useState, type FormEvent } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from './ui/button';
import './LoginPage.css';
import { loginErrorMessage, sendPasswordReset, signInWithCredentials } from '../auth/embeddedLogin';

const features = ['SECURE ACCESS', 'COLLABORATIVE MATRIX', 'PORTFOLIO TRANSPARENCY'];

export function LoginPage() {
    const { loginWithRedirect, error: authError } = useAuth0();
    const emailInput = useRef<HTMLInputElement>(null);
    const passwordInput = useRef<HTMLInputElement>(null);
    const working = useRef(false);
    const [showPassword, setShowPassword] = useState(false);
    const [isRedirecting, setIsRedirecting] = useState(false);
    const [isResetting, setIsResetting] = useState(false);
    const [resetMode, setResetMode] = useState(false);
    const [notice, setNotice] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const handleLogin = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (working.current) return;
        working.current = true;
        setError(null);
        setNotice(null);
        setIsRedirecting(true);
        const email = emailInput.current?.value.trim() || '';
        const password = passwordInput.current?.value || '';
        if (passwordInput.current) passwordInput.current.value = '';
        try {
            await loginWithRedirect({
                appState: { returnTo: location.pathname + location.search + location.hash },
                openUrl: (url) => signInWithCredentials(url, email, password),
            });
        } catch (failure) {
            setError(loginErrorMessage(failure));
            setIsRedirecting(false);
            working.current = false;
        }
    };

    const handlePasswordReset = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (working.current || !emailInput.current?.reportValidity()) return;
        working.current = true;
        setIsResetting(true);
        setError(null);
        setNotice(null);
        try {
            await sendPasswordReset(emailInput.current.value);
            setNotice('If an account exists for this email, you will receive password reset instructions.');
        } catch { setError('Unable to request a password reset. Please wait and try again.'); }
        finally { setIsResetting(false); working.current = false; }
    };

    return (
        <div className="entry-page" data-node-id="1118:2699">
            <header className="entry-header">
                <div className="entry-brand">
                    <span className="entry-brand-name">Portfolio Transparency</span>
                    <span className="entry-pilot">PILOT</span>
                </div>
                <p className="entry-sandbox">Lantmännen Unibake Experimental Sandbox</p>
            </header>
            <main className="entry-main">
                <section className="entry-hero" aria-labelledby="entry-title">
                    <div className="entry-statement">
                        <h1 id="entry-title" className="entry-title">Optimizing our daily bread.</h1>
                        <p className="entry-description">
                            An experimental computational environment that supports product
                            transparency by connecting your requests to data.
                        </p>
                    </div>
                    <ul className="entry-features" aria-label="Platform features">
                        {features.map((feature) => (
                            <li className="entry-feature" key={feature}>
                                <img src="/assets/entry/pill-dot.svg" alt="" aria-hidden="true" />
                                {feature}
                            </li>
                        ))}
                    </ul>
                </section>
                <section className="entry-card" aria-labelledby="entry-welcome">
                    <div className="entry-logo-slot">
                        <img className="entry-logo" src="/lantmannen-unibake.svg" alt="Lantmännen Unibake" />
                    </div>
                    <div>
                        <h2 id="entry-welcome">{resetMode ? 'Reset your password' : 'Welcome'}</h2>
                        <p className="entry-card-description">{resetMode ? 'Enter your account email. We’ll send a link to choose a new password securely.' : 'Sign in to access your dashboard, test and learn.'}</p>
                    </div>
                    <form className="entry-form" onSubmit={resetMode ? handlePasswordReset : handleLogin} aria-label={resetMode ? 'Reset password' : 'Sign in'}>
                        <div data-analytics-private className="entry-fields mp-block">
                            <label className="entry-email" htmlFor="entry-email">
                                Email address *
                                <input id="entry-email" ref={emailInput} type="email" autoComplete="username" required disabled={isRedirecting || isResetting} />
                            </label>
                            {!resetMode && <div className="entry-password">
                                <label className="entry-password-label" htmlFor="entry-password">Password *</label>
                                <input id="entry-password" ref={passwordInput} type={showPassword ? 'text' : 'password'} autoComplete="current-password" required disabled={isRedirecting || isResetting} />
                                <button type="button" className="entry-password-toggle" aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword} onClick={() => setShowPassword(!showPassword)}>
                                    {showPassword ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
                                </button>
                            </div>}
                            {!resetMode && <button type="button" className="entry-reset" disabled={isRedirecting || isResetting} onClick={() => {
                                if (passwordInput.current) passwordInput.current.value = '';
                                setResetMode(true); setError(null); setNotice(null); setShowPassword(false);
                            }}>Reset password</button>}
                        </div>
                        <Button
                            type="submit"
                            className="entry-continue"
                            data-track-id={resetMode ? 'reset_password' : 'sign_in'}
                            disabled={isRedirecting || isResetting}
                            aria-busy={isRedirecting}
                        >
                            {isResetting ? 'Sending reset link…' : resetMode ? 'Send reset link' : isRedirecting ? 'Signing in…' : 'Continue'}
                        </Button>
                        {(error || (!resetMode && authError)) && <p className="entry-error" role="alert">{error || 'Sign-in could not be completed. Please try again.'}</p>}
                        {notice && <p className="entry-auth-note" role="status">{notice}</p>}
                        {resetMode && <>
                            {notice && <p className="entry-auth-note">Open the latest email, follow its link, and choose a new password. Check your spam folder if needed, then return here to sign in.</p>}
                            <button type="button" className="entry-reset" disabled={isResetting} onClick={() => { setResetMode(false); setError(null); setNotice(null); }}>Back to sign in</button>
                        </>}
                    </form>
                </section>
            </main>
        </div>
    );
}
